<template>
  <!-- Navbar -->
  <nav
    class="navbar navbar-main navbar-expand-lg position-fixed top-0 start-0 end-0 px-0 shadow-none bg-gradient-dark"
    style="z-index: 1001"
    id="navbarBlur"
    data-scroll="false"
  >
    <div class="container-fluid">
      <div
        class="collapse navbar-collapse d-flex justify-content-between"
        id="navbar"
      >
        <!-- Logo Section -->
        <div class="second-logo d-flex align-items-center">
          <div class="d-flex align-items-center">
            <a
              v-if="isAdmin && isMobile && currentRoute !== 'survey'"
              class="nav-link text-body p-0 text-white cursor-pointer"
              @click="toggleClassHidden"
            >
              <div class="sidenav-toggler-inner">
                <i class="sidenav-toggler-line"></i>
                <i class="sidenav-toggler-line"></i>
                <i class="sidenav-toggler-line"></i>
              </div>
            </a>
            <a
              class="navbar-brand m-0 ps-3 cursor-pointer"
              style="padding-bottom: 10px"
              @click="goToHome"
            >
              <img
                src="@/assets/img/logo-white.png"
                alt="Mind One"
                style="width: 100px; height: auto"
              />
            </a>
          </div>
        </div>

        <!-- User Info Section -->
        <div class="participant-info">
          <div class="text-end text-white">
            <div class="h-100">
              <h6 class="font-weight-bold text-white">
                {{ user ? `${user.first_name} ${user.last_name}` : "" }}
              </h6>
            </div>
          </div>
          <div class="ps-2">
            <div
              class="avatar avatar-lg position-relative cursor-pointer navbar-user-avatar"
              data-bs-toggle="dropdown"
              id="navbarDropdownUser"
            >
              <template v-if="user">
                <ImageAuth
                  v-if="user.picture_id"
                  :imageId="Number(user.picture_id)"
                  class="w-80 shadow-sm"
                  :alt="user.username"
                />
                <ImageAuth
                  v-else
                  :imageId="30"
                  class="w-80 shadow-sm"
                  alt="user"
                />
              </template>
            </div>
            <!-- Dropdown Menu -->
            <ul
              class="dropdown-menu dropdown-menu-end mt-0 shadow"
              aria-labelledby="navbarDropdownUser"
            >
              <li v-if="user" @click="handleProfileView">
                <div class="dropdown-item d-flex cursor-pointer">
                  <i class="material-icons text-xl">settings</i>
                  <span class="px-2">{{
                    $te("settings") ? $t("settings") : "Settings"
                  }}</span>
                </div>
              </li>
              <li @click="openContactModal">
                <div class="dropdown-item d-flex cursor-pointer">
                  <i class="material-icons text-xl">support_agent</i>
                  <span class="px-2">{{
                    $te("contact_support")
                      ? $t("contact_support")
                      : "Contact Support"
                  }}</span>
                </div>
              </li>
              <li @click="handleTermsView">
                <div class="dropdown-item d-flex cursor-pointer">
                  <i class="material-icons text-xl">description</i>
                  <span class="px-2">Terms and Conditions</span>
                </div>
              </li>
              <li
                v-if="isSystemAdmin || isProjectAdmin"
                @click="handleSupportTicket"
              >
                <div class="dropdown-item d-flex cursor-pointer">
                  <i class="material-icons text-xl">confirmation_number</i>
                  <span class="px-2">{{
                    $te("support_ticket") ? $t("support_ticket") : "Support"
                  }}</span>
                </div>
              </li>
              <li>
                <a
                  @click.prevent="handleLogout"
                  class="dropdown-item d-flex cursor-pointer"
                >
                  <i class="material-icons text-xl">logout</i>
                  <span class="px-2">{{
                    $te("logout") ? $t("logout") : "Logout"
                  }}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { useAuthStore } from "@/stores";
import { useCompanyStore } from "@/stores/companyStore";
import ImageAuth from "@/components/ImageAuth.vue";
import { usePermissions } from "@/composables/usePermissions";
import { useContactModal } from "@/composables/useContactModal";
import { openPeppermintWidget } from "@/composables/usePeppermintWidget";

export default {
  name: "Navbar",
  setup() {
    const {
      isUser,
      isSystemAdmin,
      isCompanyAdmin,
      isKeyAccount,
      isProjectAdmin,
      isQuestionnaireCreator,
      keyUserTypes,
    } = usePermissions();
    const { openContactModal } = useContactModal();
    const companyStore = useCompanyStore();

    return {
      isUser,
      isSystemAdmin,
      isCompanyAdmin,
      isKeyAccount,
      isProjectAdmin,
      isQuestionnaireCreator,
      openContactModal,
      keyUserTypes,
      companyStore,
    };
  },
  components: {
    ImageAuth,
  },
  data() {
    const initialIsMobile = window.innerWidth < 1200;
    return {
      user: useAuthStore().user,
      isMobile: initialIsMobile,
      currentRoute: "",
      previousIsMobile: initialIsMobile,
    };
  },
  computed: {
    isAdmin() {
      const result =
        this.user &&
        (this.isSystemAdmin ||
          this.isCompanyAdmin ||
          this.isKeyAccount ||
          this.isProjectAdmin ||
          this.isQuestionnaireCreator);
      return result;
    },
  },
  methods: {
    toggleClassHidden() {
      const body = document.body;
      body.classList.toggle("g-sidenav-hidden");
      body.classList.toggle("g-sidenav-pinned");
    },
    async handleLogout() {
      const authStore = useAuthStore();
      await authStore.logout();
    },
    async handleProfileView() {
      this.$router.push({ name: "profile", params: { id: this.user.user_id } });
    },
    handleSupportTicket() {
      openPeppermintWidget();
    },
    async handleTermsView() {
      this.$router.push({ name: "agree2terms" });
    },
    goToHome() {
      this.$router.push("/");
    },
    handleResize() {
      const wasMobile = this.previousIsMobile;
      this.isMobile = window.innerWidth < 1200;

      // If transitioning from mobile to desktop, reset sidebar classes
      if (wasMobile && !this.isMobile) {
        const body = document.body;
        body.classList.remove("g-sidenav-hidden");
        body.classList.remove("g-sidenav-pinned");
      }

      this.previousIsMobile = this.isMobile;
    },
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
  },
};
</script>

<style scoped>
@media only screen and (min-width: 1200px) {
  .second-logo {
    display: none;
  }
}

/* Navbar User Avatar Border */
.navbar-user-avatar {
  width: 2.5rem !important;
  height: 2.5rem !important;
  border: 2px solid #ffffff !important;
  border-radius: 50% !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

.navbar-user-avatar img {
  border-radius: 50% !important;
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
}
</style>
